/**
 * useScrollProgress — Continuous scroll-linked transforms for Cogo nodes.
 *
 * Uses Framer Motion's useScroll + useTransform to map scroll position
 * to visual properties (opacity, translateY, scale, blur, etc.).
 *
 * Use cases: parallax, navbar glass effects, progress-based reveals.
 */
import { useRef } from 'react'
import { useScroll, useTransform, type MotionValue } from 'motion/react'
import type { AnimationConfig, ScrollProgressProps } from '../core/types'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface UseScrollProgressResult {
  ref: React.RefObject<HTMLDivElement | null>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  style: Record<string, MotionValue<any>> | null
  isScrollProgress: boolean
}

/* ------------------------------------------------------------------ */
/*  Hook                                                               */
/* ------------------------------------------------------------------ */

/**
 * Scroll-progress hook for Cogo nodes.
 *
 * When animation.scroll.trigger === "progress" AND animation.scrollProgress
 * is defined, this hook maps scroll position → visual properties.
 *
 * Returns motion style values that can be spread onto a motion.div.
 */
export function useScrollProgress(
  animation: AnimationConfig | undefined,
): UseScrollProgressResult {
  const ref = useRef<HTMLDivElement | null>(null)

  const isScrollProgress =
    animation?.scroll?.trigger === 'progress' &&
    animation?.scrollProgress != null

  const offset = animation?.scroll?.offset ?? ['start end', 'end start']
  const props = animation?.scrollProgress

  const { scrollYProgress } = useScroll({
    target: isScrollProgress ? ref : undefined,
    // Framer Motion expects specific string literal unions; our config uses plain strings
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    offset: offset as any,
  })

  // Build motion style values — each property maps [0,1] → [from, to]
  const style = isScrollProgress && props
    ? buildScrollStyle(scrollYProgress, props)
    : null

  return { ref, style, isScrollProgress }
}

/* ------------------------------------------------------------------ */
/*  Style builder                                                      */
/* ------------------------------------------------------------------ */

function buildScrollStyle(
  progress: MotionValue<number>,
  props: ScrollProgressProps,
  // MotionValue<T> is invariant — MotionValue<number> is not assignable to MotionValue<number|string>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, MotionValue<any>> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const style: Record<string, MotionValue<any>> = {}

  if (props.opacity) {
    style.opacity = useTransform(progress, [0, 1], props.opacity)
  }
  if (props.translateY) {
    style.y = useTransform(progress, [0, 1], props.translateY)
  }
  if (props.translateX) {
    style.x = useTransform(progress, [0, 1], props.translateX)
  }
  if (props.scale) {
    style.scale = useTransform(progress, [0, 1], props.scale)
  }
  if (props.rotate) {
    style.rotate = useTransform(progress, [0, 1], props.rotate)
  }
  if (props.blur) {
    style.filter = useTransform(
      progress,
      [0, 1],
      [`blur(${props.blur[0]}px)`, `blur(${props.blur[1]}px)`],
    )
  }

  return style
}
